/* ═══════════════════════════════════════════════════════════════
   ALTERLY — Runtime Graph Indexes
   Maintains O(1) adjacency and dependency indexes from canonical records.
   ═══════════════════════════════════════════════════════════════ */

import type { CanonicalGraph, EdgeId, NodeId, PortId, RuntimeGraphIndexes } from './types';
import { computeTopologicalOrder } from './ordering';

/**
 * Creates a unique key for a port on a specific node.
 */
export function getPortKey(nodeId: NodeId, portId: PortId): string {
  return `${nodeId}:${portId}`;
}

/**
 * Builds all runtime indexes from scratch for a canonical graph.
 */
export function buildRuntimeIndexes(graph: CanonicalGraph): RuntimeGraphIndexes {
  const outgoingEdges: Record<NodeId, EdgeId[]> = {};
  const incomingEdges: Record<NodeId, EdgeId[]> = {};
  const portConnections: Record<string, EdgeId[]> = {};
  const directDownstream: Record<NodeId, NodeId[]> = {};
  const directUpstream: Record<NodeId, NodeId[]> = {};

  for (const nodeId of Object.keys(graph.nodes)) {
    outgoingEdges[nodeId] = [];
    incomingEdges[nodeId] = [];
    directDownstream[nodeId] = [];
    directUpstream[nodeId] = [];
  }

  for (const edge of Object.values(graph.edges)) {
    const { id, sourceNodeId, sourcePortId, targetNodeId, targetPortId } = edge;

    // Outgoing & incoming by node
    if (outgoingEdges[sourceNodeId]) {
      outgoingEdges[sourceNodeId].push(id);
    } else {
      outgoingEdges[sourceNodeId] = [id];
    }

    if (incomingEdges[targetNodeId]) {
      incomingEdges[targetNodeId].push(id);
    } else {
      incomingEdges[targetNodeId] = [id];
    }

    // Direct downstream & upstream
    if (directDownstream[sourceNodeId]) {
      if (!directDownstream[sourceNodeId].includes(targetNodeId)) {
        directDownstream[sourceNodeId].push(targetNodeId);
      }
    } else {
      directDownstream[sourceNodeId] = [targetNodeId];
    }

    if (directUpstream[targetNodeId]) {
      if (!directUpstream[targetNodeId].includes(sourceNodeId)) {
        directUpstream[targetNodeId].push(sourceNodeId);
      }
    } else {
      directUpstream[targetNodeId] = [sourceNodeId];
    }

    // Node-scoped Port mappings
    const sourcePortKey = getPortKey(sourceNodeId, sourcePortId);
    const targetPortKey = getPortKey(targetNodeId, targetPortId);

    if (portConnections[sourcePortKey]) {
      portConnections[sourcePortKey].push(id);
    } else {
      portConnections[sourcePortKey] = [id];
    }

    if (portConnections[targetPortKey]) {
      portConnections[targetPortKey].push(id);
    } else {
      portConnections[targetPortKey] = [id];
    }
  }

  const topoResult = computeTopologicalOrder(graph);
  const topologicalOrder = topoResult.success ? topoResult.order : null;

  return {
    outgoingEdges,
    incomingEdges,
    portConnections,
    directDownstream,
    directUpstream,
    topologicalOrder,
  };
}

/**
 * Returns the complete transitive set of all downstream nodes dependent on a node.
 * Used for surgical execution dirty-propagation without touching the canonical records.
 */
export function getTransitiveDownstream(
  indexes: RuntimeGraphIndexes,
  startNodeId: NodeId
): Set<NodeId> {
  const downstream = new Set<NodeId>();
  const queue: NodeId[] = [startNodeId];

  while (queue.length > 0) {
    const current = queue.shift()!;
    const targets = indexes.directDownstream[current] || [];
    for (const target of targets) {
      if (!downstream.has(target)) {
        downstream.add(target);
        queue.push(target);
      }
    }
  }

  return downstream;
}
